import { useEffect, useId, useRef, useState, type RefObject } from 'react'
import { toast } from '@/lib/toast'
import { ConfirmDialog } from '@/components/patterns/ConfirmDialog'
import type { RelationRef } from '../timeline/types'
import { listRelationAgreements } from './api'

/** The checkbox is mounted per opening (the dialog content unmounts on close), so it starts checked every time. */
function EndAgreementsChoice({ running, choiceRef }: { running: number; choiceRef: RefObject<boolean> }) {
  const id = useId()
  const [checked, setChecked] = useState(true)
  useEffect(() => {
    choiceRef.current = true
  }, [choiceRef])
  return (
    <label htmlFor={id} className="mt-3 flex items-start gap-2 text-sm">
      <input
        id={id}
        type="checkbox"
        className="mt-1"
        checked={checked}
        onChange={(event) => {
          choiceRef.current = event.target.checked
          setChecked(event.target.checked)
        }}
      />
      Ook de {running === 1 ? '1 lopende afspraak' : `${running} lopende afspraken`} beëindigen
    </label>
  )
}

/**
 * Archiving a relation (spec-63 E4): when it has running agreements of its own, ask whether to end
 * them too (checked by default). `onConfirm(true)` sends `end_agreements`.
 */
export function ArchiveRelationDialog({
  open,
  onOpenChange,
  relation,
  name,
  onConfirm,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  relation: RelationRef
  name: string
  onConfirm: (endAgreements: boolean) => void
}) {
  const [running, setRunning] = useState(0)
  const [loaded, setLoaded] = useState(false)
  const choiceRef = useRef(true)
  const { kind, id } = relation

  useEffect(() => {
    if (!open) return
    let cancelled = false
    listRelationAgreements({ kind, id })
      .then((answer) => {
        if (cancelled) return
        // Same set as the backend's end_for_relation: active and paused agreements.
        setRunning(answer.own.filter((agreement) => agreement.status === 'actief' || agreement.status === 'gepauzeerd').length)
        setLoaded(true)
      })
      .catch(() => {
        if (!cancelled) toast.error('De lopende afspraken konden niet worden opgehaald. Sluit dit venster en probeer het opnieuw.')
      })
    return () => {
      cancelled = true
      setLoaded(false)
    }
  }, [open, kind, id])

  return (
    <ConfirmDialog
      open={open}
      onOpenChange={onOpenChange}
      title={`${name} archiveren?`}
      description="De relatie verdwijnt uit de lijsten; je kunt haar later weer actief maken."
      confirmLabel="Archiveren"
      destructive={false}
      confirmDisabled={!loaded}
      onConfirm={() => onConfirm(running > 0 && choiceRef.current)}
    >
      {running > 0 ? <EndAgreementsChoice running={running} choiceRef={choiceRef} /> : null}
    </ConfirmDialog>
  )
}
